import React, { useState, useEffect, useCallback, useRef } from "react";
import { Spinner, SpinnerSize, Stack } from "@fluentui/react";
import CleaningServicesOutlined from "@mui/icons-material/CleaningServicesOutlined";
import Close from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import DOMPurify from "dompurify";
import { v4 as uuidv4 } from "uuid";

import styles from "./Chat.module.css";

import {
  callConversationApi,
  pollJobStatus,
  ChatMessage,
  ChatResponse,
  ConversationRequest,
  JobStatus,
  historyDeleteAll,
  historyRead,
  historyUpdate,
  getUseCases,
  invalidateUseCasesCache,
} from "../../api";
import { useUser } from "../../providers/UserProvider";
import { Answer } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import Layout from "../../components/Layout/Layout";
import { ERROR, ASSISTANT, INTENT } from "./types";
import { parseAnswer } from "../../components/Answer/AnswerParser";
import { toPng } from "html-to-image";
import ChatHistoryPanel from "../../components/ChatHistory/ChatHistoryPanel";
import { useChatHistory } from "./useChatHistory";
import { useChat } from "./useChat";
import { useFileUpload } from "./useFileUpload";
import { useCitationIntentPanel } from "./useCitationIntentPanel";
import { formatMessageDate } from "../../utils/helpers";
import { useUseCaseContext } from "../../hooks/UseCaseContext";
import { useMetadataContext } from "../../providers/MetadataProvider";
import {
  DEFAULT_USE_CASE,
  DEFAULT_USE_CASE_NAME,
} from "../../utils/constants";
import Footer from "../../components/Footer/Footer";
import ChatLander from "../../components/ChatLander/ChatLander";
import Banner from "../../components/Banner/Banner";
import { multiLingualSpeechRecognizer } from "../../utils/SpeechToText";
import {
  ResultReason,
  CancellationReason,
} from "microsoft-cognitiveservices-speech-sdk";
import { shouldShowMaintenanceBanner } from "../../utils/maintenance";

const SILENCE_TIMEOUT_MS = 10000;
const SILENCE_WARNING_MS = 7000;
const TOKEN_REFRESH_MS = 9 * 60 * 1000;

const Chat: React.FC = () => {
  const [dgName, setDgName] = useState('INTPA')
  const [dgDisplayName, setDgDisplayName] = useState('INTPA')
  const { dg: contextDg, displayName: contextDisplayName } = useUser();
  const { useCase, setUseCase } = useUseCaseContext();
  const { selectedMetadata, excludedMetadata, handleFetchMetadata } = useMetadataContext();

  const {
    abortFuncs,
    answers,
    chatMessageStreamEnd,
    conversationId,
    isAssistantAPILoading,
    isLoading,
    lastAnswerRef,
    lastQuestionRef,
    questionToSend,
    showLoadingMessage,
    streamingMessageId,
    userUploadConfig,
    maintenanceConfig,
    handleQuestionToSend,
    onStopGenerating,
    resetLoadingState,
    resetQuestionToSend,
    addPendingRequest,
    removePendingRequest,
    setPendingAsyncAnswers,
    getPendingAsyncAnswers,
    restorePendingState,
    setAnswers,
    setConversationId,
    setIsLoading,
    setQuestionToSend,
    setShowLoadingMessage,
    setStreamingMessageId,
  } = useChat();

  const {
    chatHistory,
    clearing,
    clearSearch,
    clearingError,
    fetchingChatHistory,
    fetchingConvMessages,
    handleSearchHistory,
    hideClearAllDialog,
    isSearchActive,
    searchResults,
    searchingChatHistory,
    selectedConvId,
    showHistoryBtn,
    showHistoryPanel,
    toggleSpinner,
    getMessagesByConvId,
    getUseCaseByConvId,
    handleFetchHistory,
    resetHistory,
    onHideClearAllDialog,
    onHistoryTitleChange,
    setChatHistory,
    setClearing,
    setClearingError,
    setFetchingConvMessages,
    setMessagesByConvId,
    setSearchResults,
    setSelectedConvId,
    setShowHistoryPanel,
    setToggleSpinner,
    toggleClearAllDialog,
  } = useChatHistory();

  const {
    uploadedFiles,
    uploadedFilesByConvId,
    fetchConversationFiles,
    setUploadedFiles,
  } = useFileUpload();

  const {
    activeCitation,
    activeIntent,
    currentIntentContent,
    isCitationPanelOpen,
    isIntentPanelOpen,
    setActiveCitation,
    setCurrentIntentContent,
    setIsCitationPanelOpen,
    setIsIntentPanelOpen,
    onShowCitation,
    onShowIntent,
    parseCitationFromMessage,
  } = useCitationIntentPanel();

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [useInternet, setUseInternet] = useState(true);
  const [folderNumber, setFolderNumber] = useState(1);

  const [isListening, setIsListening] = useState(false);
  const [isSilenceWarning, setIsSilenceWarning] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const recognizerRef = useRef<Awaited<
    ReturnType<typeof multiLingualSpeechRecognizer>
  > | null>(null);
  const confirmedTextRef = useRef<string>("");
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const silenceWarningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const tokenRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const isActiveRef = useRef(false);
  const isListeningRef = useRef(false);

  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  useEffect(() => {
    if (contextDg) {
      setDgName(contextDg);
      setDgDisplayName(contextDisplayName ?? contextDg);
    }
  }, [contextDg, contextDisplayName]);

  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicDeviceId, setSelectedMicDeviceId] = useState<
    string | undefined
  >(undefined);

  const fetchMicDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setMicDevices(devices.filter((d) => d.kind === "audioinput"));
    } catch {
      // ignore — brak uprawnień lub brak urządzeń
    }
  }, []);
  const [isSavingToDB, setIsSavingToDB] = useState(false);
  const [visualUseCase, setVisualUseCase] = useState(DEFAULT_USE_CASE_NAME);
  const pendingAnswersRef = React.useRef<ChatMessage[] | null>(null);
  const isSelectingConversationRef = React.useRef(false);
  const activeConvIdRef = useRef<string>(conversationId);

  useEffect(() => {
    activeConvIdRef.current = selectedConvId || conversationId;
  }, [selectedConvId, conversationId]);

  useEffect(() => {
    if (!fetchingConvMessages) {
      chatMessageStreamEnd.current?.scrollIntoView({ behavior: "instant" });
    }
  }, [fetchingConvMessages]);

  useEffect(() => {
    if (isSelectingConversationRef.current) {
      isSelectingConversationRef.current = false;
      return;
    }
    clearChatState();
    if (pendingAnswersRef.current) {
      setAnswers(pendingAnswersRef.current);
      pendingAnswersRef.current = null;
    }
  }, [useCase]);

  const handleShowIntent = (intent: string) => {
    setShowHistoryPanel(false);
    onShowIntent(intent);
  };

  const handleStopGenerating = () => {
    onStopGenerating();
    setCurrentIntentContent("");
  };

  const handleSelectUseCase = (
    title: string,
    key: string,
    confirmationMessage?: string,
  ) => {
    setVisualUseCase(title);
    if (confirmationMessage) {
      const emptyTool: ChatMessage = {
        role: "tool",
        content: "",
        id: uuidv4(),
        date: new Date().toISOString(),
      };
      const convConfirmationMessage: ChatMessage = {
        role: "assistant",
        content: confirmationMessage,
        id: uuidv4(),
        date: new Date().toISOString(),
      };
      pendingAnswersRef.current = [emptyTool, convConfirmationMessage];
    }
    setUseCase(key);
  };

  const onClearAllChatHistory = async () => {
    setToggleSpinner(true);
    setClearing(true);
    const response = await historyDeleteAll();
    if (!response.ok) {
      setClearingError(true);
    } else {
      setChatHistory([]);
      toggleClearAllDialog();
      setShowDeletePopup(false);
      setAnswers([]);
      setSelectedConvId("");
    }
    setClearing(false);
    setToggleSpinner(false);
  };

  const clearChatState = () => {
    resetLoadingState();
    lastQuestionRef.current = "";
    setActiveCitation(undefined);
    setAnswers([]);
    setStreamingMessageId(null);
    setCurrentIntentContent("");
    setConversationId(uuidv4());
    setSelectedConvId("");
    resetQuestionToSend();
  };

  const handleDgSwitch = (newDg: string, displayName?: string) => {
    setDgName(newDg);
    setDgDisplayName(displayName ?? newDg);
    invalidateUseCasesCache();
    handleFetchMetadata();
    resetHistory();
    clearChatState();
  };

  const onClearCurrentChat = () => {
    clearChatState();
    setVisualUseCase(DEFAULT_USE_CASE_NAME);
    setUseCase(DEFAULT_USE_CASE);
  };

  const onHistoryDelete = useCallback(
    (id: string) => {
      setChatHistory((prevHistory) =>
        prevHistory.filter((conversation) => conversation.id !== id),
      );
      setSearchResults((prevResults) =>
        prevResults.filter((conversation) => conversation.id !== id),
      );
      if (id === selectedConvId) {
        onClearCurrentChat();
      }
    },
    [selectedConvId, onClearCurrentChat],
  );

  const handleFileDeleted = (uploadedFileName: string) => {
    setUploadedFiles((prev) =>
      prev.filter((f) => f.uploadedFileName !== uploadedFileName),
    );
  };

  const onRefetchConversation = async () => {
    const idToFetch = selectedConvId || conversationId;
    if (idToFetch) {
      const messages = await historyRead(idToFetch);
      if (messages && messages.length > 0) {
        setAnswers(messages);
        // Update the messages in chat history as well
        setMessagesByConvId(idToFetch, messages);
      }
    }
  };

  const updateConversationRecency = (convId: string) => {
    if (!convId) return;
    setChatHistory((prevHistory) => {
      const convIndex = prevHistory.findIndex((c) => c.id === convId);
      if (convIndex === -1) return prevHistory;
      const updatedHistory = [...prevHistory];
      const [conversation] = updatedHistory.splice(convIndex, 1);
      conversation.updatedAt = new Date().toISOString();
      updatedHistory.unshift(conversation);
      return updatedHistory;
    });
  };

  const onSelectConversation = async (id: string) => {
    if (!id) {
      console.error("No conversation Id found");
      return;
    }
    if (isLoading) {
      resetLoadingState();
    }
    setConversationId(id);
    const messages = getMessagesByConvId(id);
    const convUseCase = getUseCaseByConvId(id);
    const targetUseCase =
      convUseCase && convUseCase !== "" ? convUseCase : DEFAULT_USE_CASE;
    if (targetUseCase !== useCase) {
      isSelectingConversationRef.current = true;
    }
    if (convUseCase && convUseCase !== "") {
      setUseCase(convUseCase);
      const useCases = await getUseCases();
      const displayName =
        useCases.find((uc) => uc.id === convUseCase)?.name ||
        DEFAULT_USE_CASE_NAME;
      setVisualUseCase(displayName);
    } else {
      setUseCase(DEFAULT_USE_CASE);
      setVisualUseCase(DEFAULT_USE_CASE_NAME);
    }
    if (messages.length === 0) {
      setFetchingConvMessages(true);
      const responseMessages = await historyRead(id);
      fetchConversationFiles(id);
      setAnswers(responseMessages);
      setMessagesByConvId(id, responseMessages);
      setFetchingConvMessages(false);
    } else {
      setAnswers(messages);
      setUploadedFiles(uploadedFilesByConvId[id]);
    }
    setSelectedConvId(id);
    restorePendingState(id);
  };

  const stopListening = useCallback(() => {
    isActiveRef.current = false;
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (silenceWarningTimerRef.current)
      clearTimeout(silenceWarningTimerRef.current);
    if (tokenRefreshIntervalRef.current)
      clearInterval(tokenRefreshIntervalRef.current);
    recognizerRef.current?.stopContinuousRecognitionAsync();
    recognizerRef.current?.close();
    recognizerRef.current = null;
    confirmedTextRef.current = "";
    setIsListening(false);
    setIsSilenceWarning(false);
  }, []);

  useEffect(() => {
    return () => {
      stopListening();
    };
  }, [stopListening]);

  const resetSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (silenceWarningTimerRef.current)
      clearTimeout(silenceWarningTimerRef.current);
    setIsSilenceWarning(false);
    silenceWarningTimerRef.current = setTimeout(
      () => setIsSilenceWarning(true),
      SILENCE_WARNING_MS,
    );
    silenceTimerRef.current = setTimeout(stopListening, SILENCE_TIMEOUT_MS);
  }, [stopListening]);

  const startListening = useCallback(
    async (deviceId: string | undefined, initialText: string) => {
      try {
        confirmedTextRef.current = initialText;
        setMicError(null);
        recognizerRef.current = await multiLingualSpeechRecognizer(deviceId);
        fetchMicDevices();
        setIsListening(true);

        recognizerRef.current.recognizing = (_s, e) => {
          if (!isActiveRef.current || !e.result.text) return;
          resetSilenceTimer();
          const prefix = confirmedTextRef.current
            ? confirmedTextRef.current + " "
            : "";
          handleQuestionToSend(prefix + e.result.text);
        };

        recognizerRef.current.recognized = (_s, e) => {
          if (!isActiveRef.current) return;
          if (
            e.result.reason === ResultReason.RecognizedSpeech &&
            e.result.text
          ) {
            resetSilenceTimer();
            confirmedTextRef.current = confirmedTextRef.current
              ? confirmedTextRef.current + " " + e.result.text
              : e.result.text;
            handleQuestionToSend(confirmedTextRef.current);
          }
        };

        recognizerRef.current.canceled = (_s, e) => {
          if (!isActiveRef.current) return;
          if (e.reason === CancellationReason.Error) {
            stopListening();
            setMicError("Voice input error. Please try again.");
          }
        };

        isActiveRef.current = true;
        recognizerRef.current.startContinuousRecognitionAsync();
        resetSilenceTimer();

        tokenRefreshIntervalRef.current = setInterval(async () => {
          try {
            const response = await fetch("/api/speech");
            if (response.ok && recognizerRef.current) {
              const { token } = await response.json();
              recognizerRef.current.authorizationToken = token;
            }
          } catch {
            /* ignore */
          }
        }, TOKEN_REFRESH_MS);
      } catch (err) {
        console.error("Speech recognition error:", err);
        setIsListening(false);
        setMicError(
          "Voice input is currently unavailable. Please try again later.",
        );
      }
    },
    [fetchMicDevices, resetSilenceTimer, stopListening, handleQuestionToSend],
  );

  const onMicrophoneClick = async () => {
    if (isListening) {
      stopListening();
      return;
    }
    await startListening(selectedMicDeviceId, questionToSend.content ?? "");
  };

  useEffect(() => {
    if (!isListeningRef.current) return;
    const currentText = confirmedTextRef.current;
    stopListening();
    startListening(selectedMicDeviceId, currentText);
  }, [selectedMicDeviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSend = async (question: string) => {
    stopListening();
    lastQuestionRef.current = question;

    setIsLoading(true);
    setShowLoadingMessage(true);
    setCurrentIntentContent(""); // Clear any previous intent display
    updateConversationRecency(selectedConvId || conversationId);
    const abortController = new AbortController();
    abortFuncs.current.unshift(abortController);
    let isAsyncJob = false;
    const requestConvId = selectedConvId || conversationId;
    const isStillActiveConv = () => activeConvIdRef.current === requestConvId;
    const requestIsNew = !selectedConvId;
    addPendingRequest(requestConvId, question);

    const previousMessages =
      questionToSend.id !== ""
        ? answers.slice(
            0,
            answers.findIndex((message) => message.id === questionToSend.id),
          )
        : answers;

    const newUserMessage: ChatMessage = {
      role: "user",
      content: question,
      id: questionToSend.id || uuidv4(),
      date: new Date().toISOString(),
    };

    const request: ConversationRequest = {
      id: selectedConvId || conversationId,
      messages: [...previousMessages, newUserMessage].filter(
        (messageObj) => messageObj.role !== ERROR,
      ),
    };
    let result = {} as ChatResponse;
    try {
      const response = await callConversationApi(
        request,
        abortController.signal,
        useCase,
        useInternet,
        excludedMetadata,
        folderNumber,
      );

      if (response.status === 202) {
        // Long-running use case: poll for result
        isAsyncJob = true;
        const data = await response.json();
        const jobId: string = data.job_id;
        const estimatedMinutesFromServer =
          typeof data.estimated_minutes === "number"
            ? data.estimated_minutes
            : undefined;

        const formatWorkflowStatusMessage = (job: JobStatus): string => {
          const orderedSteps = (job.steps ?? []).length
            ? [...(job.steps ?? [])].sort((a, b) => a.order - b.order)
            : [];
          const totalSteps = orderedSteps.length;

          const activeStepId = job.current_step?.id ?? orderedSteps.at(-1)?.id;
          const activeIndex = orderedSteps.findIndex(
            (s) => s.id === activeStepId,
          );
          const safeActiveIndex =
            activeIndex >= 0
              ? activeIndex
              : Math.max(0, orderedSteps.length - 1);
          const activeStep = orderedSteps[safeActiveIndex];

          const nextApprovalStep = orderedSteps
            .slice(safeActiveIndex + 1)
            .find((s) => s.requires_approval);

          const estimatedMinutes = (() => {
            if (!activeStep) return estimatedMinutesFromServer;
            if (activeStep.requires_approval) return 1;
            if (nextApprovalStep) {
              const nextApprovalIndex = orderedSteps.findIndex(
                (s) => s.id === nextApprovalStep.id,
              );
              return Math.max(1, nextApprovalIndex - safeActiveIndex);
            }
            const remainingSteps = orderedSteps.length - safeActiveIndex - 1;
            return Math.max(1, remainingSteps || 1);
          })();

          const stepName = activeStep?.name ?? job.current_step?.name;
          const needsConfirmation =
            job.current_step?.requires_approval ??
            activeStep?.requires_approval;

          // Use server-provided total time if available, otherwise estimate
          const totalEstimatedMinutes =
            estimatedMinutesFromServer ?? estimatedMinutes;

          // Build the message based on state
          if (needsConfirmation) {
            return `⚠️ Confirmation required

This workflow includes **${totalSteps} steps** and is **expected to take about ${totalEstimatedMinutes} minutes** in total.

Current step: **${stepName}**

The output for this step requires your approval. Please wait for the output to be ready. Once you approve this step, the workflow will continue.`;
          }

          if (nextApprovalStep) {
            return `Your request is being processed

This workflow includes **${totalSteps} steps** and is **expected to take about ${totalEstimatedMinutes} minutes** until the next approval step.

Current step: **${stepName}**

You can leave this chat and continue working on something else. The next step (**${nextApprovalStep.name}**) will require your confirmation. I will send the output of the step requiring your approval once it is ready.`;
          }

          return `Your request is being processed

This workflow includes **${totalSteps} steps** and is **expected to take about ${totalEstimatedMinutes} minutes** in total.

Current step: **${stepName}**

You can leave this chat and continue working on something else. Once the workflow is complete, I will send the output here in this conversation.`;
        };

        const initialJobState: JobStatus = {
          job_id: jobId,
          status: "queued",
          result: null,
          error: null,
          current_step: data.current_step ?? null,
          steps: data.steps ?? [],
        };

        const estimatedMessage = formatWorkflowStatusMessage(initialJobState);

        if (isStillActiveConv()) {
          setShowLoadingMessage(false);
          setAnswers([
            ...previousMessages,
            newUserMessage,
            {
              role: "assistant",
              content: estimatedMessage,
              id: "",
              date: new Date().toISOString(),
            },
          ]);
        }
        setPendingAsyncAnswers(requestConvId, [
          ...previousMessages,
          newUserMessage,
          {
            role: "assistant",
            content: estimatedMessage,
            id: "",
            date: new Date().toISOString(),
          },
        ]);

        pollJobStatus(
          jobId,
          async (job) => {
            if (!job.result) return;
            const pollResult: ChatResponse =
              job.result as unknown as ChatResponse;
            const updatedMessages = [
              ...previousMessages,
              newUserMessage,
              ...pollResult.choices[0].messages,
            ];
            await saveToDB(updatedMessages, requestConvId, requestIsNew);
            removePendingRequest(requestConvId);
            if (isStillActiveConv()) {
              const responseMessages = await historyRead(requestConvId);
              setAnswers(responseMessages);
              resetQuestionToSend();
              setIsLoading(false);
            }
          },
          (error) => {
            removePendingRequest(requestConvId);
            if (isStillActiveConv()) {
              setAnswers([
                ...previousMessages,
                newUserMessage,
                {
                  role: "error",
                  content: error,
                  id: "",
                  date: "",
                },
              ]);
              setIsLoading(false);
            }
          },
          (job) => {
            const statusContent = formatWorkflowStatusMessage(job);
            const updatedAnswers = (() => {
              const current = getPendingAsyncAnswers(requestConvId) || [];
              const updated = [...current];
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].role === "assistant") {
                  updated[i] = {
                    ...updated[i],
                    content: statusContent,
                    date: new Date().toISOString(),
                  };
                  return updated;
                }
              }
              return [
                ...updated,
                {
                  role: "assistant" as const,
                  content: statusContent,
                  id: "",
                  date: new Date().toISOString(),
                },
              ];
            })();
            setPendingAsyncAnswers(requestConvId, updatedAnswers);
            if (!isStillActiveConv()) return;
            setAnswers(updatedAnswers);
          },
          60000, // poll every 1 minute
          30, // max 30 attempts = 30 minutes
        );
        return abortController.abort();
      }

      if (response?.body) {
        const reader = response.body.getReader();
        let runningText = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          var text = new TextDecoder("utf-8").decode(value);
          const objects = text.split("\n");
          objects.forEach((obj) => {
            try {
              runningText += obj;
              result = JSON.parse(runningText);
              if (isStillActiveConv()) {
                setShowLoadingMessage(false);
                if (result.error) {
                  setAnswers([
                    ...previousMessages,
                    newUserMessage,
                    {
                      role: "error",
                      content: result.error,
                      id: "",
                      date: "",
                    },
                  ]);
                } else {
                  setAnswers([
                    ...previousMessages,
                    newUserMessage,
                    ...result.choices[0].messages,
                  ]);
                }
              }
              runningText = "";
            } catch {}
          });
        }
        const updatedMessages = [
          ...previousMessages,
          newUserMessage,
          ...result.choices[0].messages,
        ];
        await saveToDB(updatedMessages, requestConvId, requestIsNew);
        removePendingRequest(requestConvId);
        if (isStillActiveConv()) {
          const responseMessages = await historyRead(requestConvId);
          setAnswers(responseMessages);
        }
        resetQuestionToSend();
      }
    } catch (e) {
      if (!abortController.signal.aborted && isStillActiveConv()) {
        let errorContent =
          "An error occurred. Please try again. If the problem persists, please contact the site administrator.";

        if (e instanceof Error) {
          errorContent = JSON.parse(e.message) || errorContent;
          setAnswers([
            ...previousMessages,
            newUserMessage,
            {
              role: "error",
              content: errorContent,
              id: "",
              date: "",
            },
          ]);
        } else {
          setAnswers([
            ...previousMessages,
            newUserMessage,
            {
              role: "error",
              content: errorContent,
              id: "",
              date: "",
            },
          ]);
        }
      }
    } finally {
      if (!isAsyncJob) {
        removePendingRequest(requestConvId);
      }
      if (!isAsyncJob && isStillActiveConv()) {
        setIsLoading(false);
      }
      if (isStillActiveConv()) {
        setShowLoadingMessage(false);
      }
      abortFuncs.current = abortFuncs.current.filter(
        (a) => a !== abortController,
      );
    }

    return abortController.abort();
  };

  const onSetShowHistoryPanel = () => {
    if (!showHistoryPanel) {
      setIsCitationPanelOpen(false);
      setIsIntentPanelOpen(false);
    }
    setShowHistoryPanel((prevState) => !prevState);
  };

  const saveToDB = async (
    messages: ChatMessage[],
    convId: string,
    isNewConv?: boolean,
  ) => {
    if (!convId || !messages.length) {
      return;
    }
    const isNewConversation =
      isNewConv !== undefined ? isNewConv : !selectedConvId;
    const isActive = activeConvIdRef.current === convId;
    if (isActive) setIsSavingToDB(true);
    await historyUpdate(messages, convId, useCase)
      .then(async (res) => {
        if (!res.ok) {
          let errorMessage = "Answers can't be saved at this time.";
          let errorChatMsg: ChatMessage = {
            id: uuidv4(),
            role: ERROR,
            content: errorMessage,
            date: new Date().toISOString(),
          };
          if (!messages) {
            if (activeConvIdRef.current === convId) {
              setAnswers([...messages, errorChatMsg]);
            }
            let err: Error = {
              ...new Error(),
              message: "Failure fetching current chat state.",
            };
            throw err;
          }
        }
        let responseJson = await res.json();
        if (isNewConversation && responseJson?.success) {
          const metaData = responseJson?.data;
          const newConversation = {
            id: metaData?.conversation_id,
            title: metaData?.title,
            messages: messages,
            date: metaData?.date,
            useCase: metaData?.use_case || "",
          };
          setChatHistory((prevHistory) => [newConversation, ...prevHistory]);
          if (activeConvIdRef.current === convId) {
            setSelectedConvId(metaData?.conversation_id);
          }
        } else if (responseJson?.success) {
          setMessagesByConvId(convId, messages);
        }
        if (activeConvIdRef.current === convId) setIsSavingToDB(false);
        return res as Response;
      })
      .catch((err) => {
        console.error("Error: while saving data", err);
        if (activeConvIdRef.current === convId) setIsSavingToDB(false);
      });
  };

  const downloadBlob = async (
    response: Response,
    convId: string,
    format: string,
  ) => {
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `conversation_${convId}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  // Build a payload of the on-screen conversation with charts captured as PNGs
  // and tables as structured data, so the export matches exactly what is shown.
  const buildRenderedPayload = async (convId: string) => {
    const messages: any[] = [];
    for (let index = 0; index < answers.length; index++) {
      const a = answers[index];
      if (a.role === "user") {
        messages.push({
          role: "user",
          date: a.date || "",
          text: a.content || "",
        });
      } else if (a.role === ASSISTANT) {
        const parsed = parseAnswer({
          answer: a.content || "",
          citations: [],
          intent: "",
        } as any);
        const charts: { image: string; title?: string }[] = [];
        for (let i = 0; i < parsed.charts.length; i++) {
          const node = document.getElementById(`msg-${index}-chart-${i}`);
          if (node) {
            try {
              const img = await toPng(node, {
                pixelRatio: 2,
                backgroundColor: "#ffffff",
              });
              charts.push({ image: img, title: parsed.charts[i].title });
            } catch {
              /* skip a chart that fails to capture */
            }
          }
        }
        messages.push({
          role: "assistant",
          date: a.date || "",
          text: parsed.markdownFormatText,
          tables: parsed.tables.map((t) => ({
            headers: t.headers,
            rows: t.rows,
          })),
          charts,
          dashboardLinks: parsed.dashboardLinks || [],
        });
      }
    }
    return { conversation_id: convId, messages };
  };

  const handleExport = async (
    conversationIdToExport: string,
    format: string,
  ) => {
    const convId = conversationIdToExport || selectedConvId || conversationId;

    if (!convId) {
      return;
    }

    // The on-screen conversation can be captured client-side (exact chat
    // visuals); otherwise fall back to server-side rendering.
    const isOpenConversation =
      answers.length > 0 && convId === (selectedConvId || conversationId);

    try {
      let response: Response | null = null;
      if (isOpenConversation) {
        try {
          const payload = await buildRenderedPayload(convId);
          response = await fetch(`/api/history/export-rendered/${format}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payload),
          });
          if (!response.ok) response = null; // fall back below
        } catch {
          response = null;
        }
      }

      if (!response) {
        response = await fetch(`/api/history/export/${format}/${convId}`, {
          method: "GET",
          credentials: "include",
        });
      }

      if (!response.ok) {
        throw new Error(`${format.toUpperCase()} export failed`);
      }

      await downloadBlob(response, convId, format);
    } catch (error) {
      console.error("Error exporting:", error);
      alert("Failed to export conversation.");
    }
  };

  return (
    <>
      {shouldShowMaintenanceBanner(maintenanceConfig) && (
        <Banner
          message={
            maintenanceConfig.maintenance_banner_message ||
            "The system will undergo maintenance soon."
          }
        />
      )}
      <Layout
        toggleSpinner={toggleSpinner}
        showHistoryBtn={showHistoryBtn}
        showHistoryPanel={showHistoryPanel}
        conversationId={selectedConvId || conversationId}
        showExport={false}
        useCaseTitle={visualUseCase}
        dgName={dgName}
        dgLabel={dgDisplayName}
        onSetShowHistoryPanel={onSetShowHistoryPanel}
        onUseInternetChange={setUseInternet}
        onExport={handleExport}
        onLogoClick={onClearCurrentChat}
        onFolderStructureChanged={setFolderNumber}
        onDgSwitch={handleDgSwitch}
      >
        <div className={styles.container}>
          <Stack horizontal className={styles.chatRoot}>
            <div
              className={`${styles.chatContainer} ${styles.MobileChatContainer}`}
            >
              {!fetchingConvMessages &&
              !lastQuestionRef.current &&
              answers.length === 0 ? (
                <ChatLander
                  visualUseCase={visualUseCase}
                  isAssistantAPILoading={isAssistantAPILoading}
                  handleSelectUseCase={handleSelectUseCase}
                  dgName={dgDisplayName}
                />
              ) : (
                <div
                  className={styles.chatMessageStream}
                  style={{ marginBottom: isLoading ? "40px" : "0px" }}
                >
                  {fetchingConvMessages && (
                    <div className={styles.fetchMessagesSpinner}>
                      <Spinner size={SpinnerSize.medium} />
                    </div>
                  )}
                  {!fetchingConvMessages &&
                    answers.map((answer, index) => (
                      <React.Fragment key={`${answer?.role}-${index}`}>
                        {answer.role === "user" ? (
                          <div
                            className={styles.chatMessageUser}
                            key={`${answer?.role}-${index}`}
                          >
                            <div className={styles.chatMessageUserFlex}>
                              <div
                                className={styles.editIcon}
                                onClick={() => setQuestionToSend(answer)}
                              >
                                <EditOutlined className={styles.editGlyph} />
                              </div>
                              <div className={styles.chatMessageUserMessage}>
                                {answer.content}
                              </div>
                            </div>
                            {answer.date && (
                              <div className={styles.chatMessageDate}>
                                {formatMessageDate(answer.date)}
                              </div>
                            )}
                          </div>
                        ) : answer.role === ASSISTANT ||
                          answer.role === INTENT ||
                          answer.role === "error" ? (
                          <div
                            className={`${styles.chatMessageGpt} ${
                              answer.role === INTENT
                                ? styles.chatMessageIntent
                                : ""
                            }`}
                            key={`${answer?.role}-${index}`}
                            ref={
                              index === answers.length - 1
                                ? lastAnswerRef
                                : undefined
                            }
                            style={{
                              // Hide intent message if there's a final assistant message after it
                              display:
                                answer.role === INTENT &&
                                answers
                                  .slice(index + 1)
                                  .some((msg) => msg.role === ASSISTANT)
                                  ? "none"
                                  : "flex",
                            }}
                          >
                            <Answer
                              answer={{
                                answer:
                                  answer.role === ASSISTANT
                                    ? answer.content
                                    : answer.role === INTENT
                                      ? answer.content
                                      : answer.content,
                                citations:
                                  answer.role === ASSISTANT
                                    ? answer.citations ||
                                      parseCitationFromMessage(
                                        answers[index - 1],
                                      ).citations
                                    : [],
                                intent:
                                  answer.role === ASSISTANT
                                    ? answer.intent ||
                                      parseCitationFromMessage(
                                        answers[index - 1],
                                      ).intent ||
                                      ""
                                    : answer.role === INTENT
                                      ? answer.intent || ""
                                      : "",
                              }}
                              isActive={false}
                              onCitationClicked={(c) =>
                                onShowCitation(
                                  c,
                                  selectedConvId || conversationId,
                                )
                              }
                              onIntentClicked={(intent) =>
                                handleShowIntent(intent)
                              }
                              index={index}
                              isFinalAnswer={
                                streamingMessageId !== answer.id &&
                                answer.role !== INTENT
                              }
                            />
                            {answer.date && (
                              <div className={styles.chatMessageDate}>
                                {formatMessageDate(answer.date)}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </React.Fragment>
                    ))}
                  {showLoadingMessage && (
                    <React.Fragment key="generating-answer">
                      <div className={styles.chatMessageUser}>
                        <div className={styles.chatMessageUserMessage}>
                          {lastQuestionRef.current}
                        </div>
                      </div>

                      {currentIntentContent ? (
                        <div
                          className={`${styles.chatMessageGpt} ${styles.chatMessageIntent}`}
                        >
                          <Answer
                            answer={{
                              answer: `🤔 ${currentIntentContent}`,
                              citations: [],
                              intent: "",
                            }}
                            onCitationClicked={() => null}
                            onIntentClicked={() => null}
                            index={-1}
                            isFinalAnswer={false}
                          />
                        </div>
                      ) : (
                        <div className={styles.chatMessageGpt}>
                          <Answer
                            answer={{
                              answer: "Generating answer...",
                              citations: [],
                              intent: "",
                            }}
                            onCitationClicked={() => null}
                            onIntentClicked={() => null}
                            index={0}
                            isFinalAnswer={false}
                          />
                        </div>
                      )}
                    </React.Fragment>
                  )}
                  <div ref={chatMessageStreamEnd} />
                </div>
              )}
              <Stack
                horizontal
                verticalAlign="center"
                className={styles.chatInput}
              >
                <div
                  className={`${styles.clearChatBroom} ${styles.mobileclearChatBroom}`}
                  style={{
                    background:
                      isLoading ||
                      (answers.length === 0 &&
                        visualUseCase === DEFAULT_USE_CASE_NAME)
                        ? "#BDBDBD"
                        : "radial-gradient(109.81% 107.82% at 100.1% 90.19%, #0F6CBD 33.63%, #2D87C3 70.31%, #8DDDD8 100%)",
                    cursor:
                      isLoading ||
                      (answers.length === 0 &&
                        visualUseCase === DEFAULT_USE_CASE_NAME)
                        ? ""
                        : "pointer",
                  }}
                  onClick={onClearCurrentChat}
                  onKeyDown={(e) =>
                    e.key === "Enter" || e.key === " "
                      ? onClearCurrentChat()
                      : null
                  }
                  aria-label="Clear session"
                  role="button"
                  tabIndex={0}
                >
                  <CleaningServicesOutlined
                    className={styles.clearChatBroomIcon}
                    aria-hidden="true"
                  />
                </div>
                <QuestionInput
                  onSend={onSend}
                  onMicrophoneClick={onMicrophoneClick}
                  isListening={isListening}
                  isSilenceWarning={isSilenceWarning}
                  micError={micError ?? undefined}
                  micDevices={micDevices}
                  selectedMicDeviceId={selectedMicDeviceId}
                  onMicDeviceChange={setSelectedMicDeviceId}
                  disabled={isLoading}
                  isSendButtonDisabled={isLoading}
                  isGenerating={isLoading}
                  onStopGenerating={handleStopGenerating}
                  placeholder={
                    visualUseCase !== DEFAULT_USE_CASE_NAME
                      ? `Enter the requested input`
                      : "Type a new question..."
                  }
                  clearOnSend
                  conversationId={selectedConvId || conversationId}
                  onRefetchConversation={onRefetchConversation}
                  onFileDeleted={handleFileDeleted}
                  onUploadedFilesChange={setUploadedFiles}
                  questionToSend={questionToSend.content}
                  questionToSendId={questionToSend.id}
                  setQuestionToSend={(question) =>
                    handleQuestionToSend(question)
                  }
                  resetQuestionToSend={resetQuestionToSend}
                  uploadedFiles={uploadedFiles}
                  userUploadConfig={userUploadConfig}
                  answers={answers}
                />
              </Stack>
            </div>
            {answers.length > 0 && isCitationPanelOpen && activeCitation && (
              <Stack.Item
                className={`${styles.citationPanel} ${styles.mobileStyles}`}
              >
                <Stack
                  horizontal
                  className={styles.citationPanelHeaderContainer}
                  horizontalAlign="space-between"
                  verticalAlign="center"
                >
                  <span className={styles.citationPanelHeader}>Citations</span>
                  <Close
                    role="button"
                    onKeyDown={(e) =>
                      e.key === " " || e.key === "Enter"
                        ? setIsCitationPanelOpen(false)
                        : () => {}
                    }
                    tabIndex={0}
                    className={styles.citationPanelDismiss}
                    onClick={() => setIsCitationPanelOpen(false)}
                  />
                </Stack>
                <h5
                  className={`${styles.citationPanelTitle} ${styles.mobileCitationPanelTitle}`}
                >
                  {activeCitation[2]}
                </h5>
                <div
                  className={`${styles.citationPanelDisclaimer} ${styles.mobileCitationPanelDisclaimer}`}
                >
                  Tables, images, and other special formatting not shown in this
                  preview. Please follow the link to review the original
                  document.
                </div>
                <div className={`${styles.citationPanelContent} ${styles.mobileCitationPanelContent}`}>
                  <ReactMarkdown
                    children={DOMPurify.sanitize(activeCitation[0])}
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  />
                </div>
              </Stack.Item>
            )}

            {answers.length > 0 && isIntentPanelOpen && (
              <Stack.Item
                className={`${styles.citationPanel} ${styles.mobileStyles}`}
              >
                <Stack
                  horizontal
                  className={styles.citationPanelHeaderContainer}
                  horizontalAlign="space-between"
                  verticalAlign="center"
                >
                  <span className={styles.citationPanelHeader}>Intent</span>
                  <Close
                    role="button"
                    onKeyDown={(e) =>
                      e.key === " " || e.key === "Enter"
                        ? setIsIntentPanelOpen(false)
                        : () => {}
                    }
                    tabIndex={0}
                    className={styles.citationPanelDismiss}
                    onClick={() => setIsIntentPanelOpen(false)}
                  />
                </Stack>
                <div className={`${styles.citationPanelContent} ${styles.mobileCitationPanelContent}`}>
                  <ReactMarkdown
                    children={DOMPurify.sanitize(activeIntent)}
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  />
                </div>
              </Stack.Item>
            )}

            <ChatHistoryPanel
              showDeletePopup={showDeletePopup}
              setshowDeletePopup={setShowDeletePopup}
              showHistoryPanel={showHistoryPanel}
              onClosePanel={() => setShowHistoryPanel(false)}
              chatHistory={chatHistory}
              displayChatHistory={isSearchActive ? searchResults : chatHistory}
              isSearchActive={isSearchActive}
              fetchingChatHistory={fetchingChatHistory}
              searchingChatHistory={searchingChatHistory}
              fetchingConvMessages={fetchingConvMessages}
              selectedConvId={selectedConvId}
              handleFetchHistory={handleFetchHistory}
              handleSearchHistory={handleSearchHistory}
              clearSearch={clearSearch}
              onSelectConversation={onSelectConversation}
              onHistoryTitleChange={onHistoryTitleChange}
              onHistoryDelete={onHistoryDelete}
              isGenerating={showLoadingMessage || isSavingToDB}
              isLoading={isLoading}
              toggleToggleSpinner={(toggler) => setToggleSpinner(toggler)}
              hideClearAllDialog={hideClearAllDialog}
              clearing={clearing}
              clearingError={clearingError}
              onClearAllChatHistory={onClearAllChatHistory}
              onHideClearAllDialog={onHideClearAllDialog}
              onExport={handleExport}
            />
          </Stack>
        </div>
        <Footer
          selectedConvId={selectedConvId}
          conversationId={conversationId}
          answers={answers}
          useCaseTitle={visualUseCase}
          showWorkflowBadge={
            answers.length !== 0 && visualUseCase === DEFAULT_USE_CASE_NAME
          }
          dgName={dgDisplayName}
        />
      </Layout>
    </>
  );
};

export default Chat;
